import React, { useEffect, useState, memo } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ArrowRightIcon, BarChart3Icon, ClapperboardIcon, DribbbleIcon, FacebookIcon, InstagramIcon, LinkedinIcon, MailIcon, MapPinIcon, MenuIcon, MessageCircleIcon, MonitorPlayIcon, MoveUpRightIcon, PaletteIcon, SendIcon, XIcon, ZapIcon } from 'lucide-react';
import { ContactForm } from '../components/ContactForm';
import { SectionHeader } from '../components/SectionHeader';
import { useScreenInit } from '../useScreenInit';
const assets = {
  ledWide: 'https://cdn.magicpatterns.com/uploads/a8ttrhUFtkpddGVu5Ybf6k/tania-mousinho-YlpfE9uCakE-unsplash.jpg',
  heroLed: 'https://cdn.magicpatterns.com/uploads/8JZgLUirSNXJVDFSsePsde/tania-mousinho-YlpfE9uCakE-unsplash.jpg',
  ledNight: 'https://cdn.magicpatterns.com/uploads/6yqL3YziwesEVWbSYViFXj/stevosdisposable-7aQqnfD-Cmg-unsplash.jpg',
  camera: 'https://cdn.magicpatterns.com/uploads/faYqpmn9fybRPvUuZQN7Ad/sirisvisual-IcwAKUhNGXs-unsplash.jpg',
  workspace: 'https://cdn.magicpatterns.com/uploads/pBLBCPTb9khEAMySE1U5iK/domenico-loia-hGV2TfOh0ns-unsplash.jpg'
};
type PortfolioCategory = 'Todos' | 'LED' | 'Diseño' | 'Digital' | 'Audiovisual';
const services = [{
  title: 'Cartelería & LED',
  description: 'Impacto que se ve a cualquier hora.',
  items: ['Pantallas LED', 'Cartelería digital', 'Instalación & soporte'],
  icon: MonitorPlayIcon,
  image: assets.ledNight
}, {
  title: 'Diseño & Branding',
  description: 'Identidades que se quedan en la memoria.',
  items: ['Identidad visual', 'Packaging', 'Diseño editorial'],
  icon: PaletteIcon,
  image: 'https://images.unsplash.com/photo-1561070791-2526d30994b5?auto=format&fit=crop&w=1000&q=85'
}, {
  title: 'Marketing Digital',
  description: 'Estrategias con foco en resultados reales.',
  items: ['Social media', 'Performance', 'Contenido'],
  icon: BarChart3Icon,
  image: assets.workspace
}, {
  title: 'Producción Audiovisual',
  description: 'Historias pensadas para detener el scroll.',
  items: ['Foto & video', 'Motion graphics', 'Coberturas'],
  icon: ClapperboardIcon,
  image: assets.camera
}];
const projects = [{
  name: 'Torre de impacto',
  category: 'LED',
  image: assets.ledNight
}, {
  name: 'Identidad Nórdica',
  category: 'Diseño',
  image: 'https://images.unsplash.com/photo-1545235617-9465d2a55698?auto=format&fit=crop&w=1000&q=85'
}, {
  name: 'Always on',
  category: 'Digital',
  image: 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?auto=format&fit=crop&w=1000&q=85'
}, {
  name: 'La toma exacta',
  category: 'Audiovisual',
  image: assets.camera
}, {
  name: 'La ciudad mira',
  category: 'LED',
  image: assets.ledWide
}, {
  name: 'Circuito urbano',
  category: 'LED',
  image: 'https://cdn.magicpatterns.com/uploads/nYPCCdBhBAep7RC6MCQUc8/tania-mousinho-6tCgnd2xNfI-unsplash.jpg'
}];
const clientLogos = ['ANIMA', 'PLAZA', 'CAMPO', 'MONTEVIDEO', 'RITMO', 'NEXO', 'ORIGEN'];
export function Home() {
  useScreenInit();
  const shouldReduceMotion = useReducedMotion();
  const [showIntro, setShowIntro] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const [filter, setFilter] = useState<PortfolioCategory>('Todos');
  useEffect(() => {
    if (shouldReduceMotion) {
      setShowIntro(false);
      return;
    }
    const introTimer = window.setTimeout(() => setShowIntro(false), 2400);
    return () => window.clearTimeout(introTimer);
  }, [shouldReduceMotion]);
  const filteredProjects = filter === 'Todos' ? projects : projects.filter(project => project.category === filter);
  const closeMenu = () => setMenuOpen(false);
  return <div className="min-h-screen w-full overflow-x-hidden bg-[#0A0A0A] text-white" data-id="element-35">
      <AnimatePresence data-id="element-36">
        {showIntro && <motion.div className="fixed inset-0 z-[100] flex items-center justify-center bg-[#0A0A0A]" initial={{
        opacity: 1
      }} exit={{
        opacity: 0,
        scale: 1.03,
        transition: {
          duration: 0.55,
          ease: [0.22, 1, 0.36, 1]
        }
      }} aria-label="Cargando Vivo Media" role="status" data-id="element-37">
            <motion.div className="flex items-center gap-4" initial={shouldReduceMotion ? false : {
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          ease: [0.22, 1, 0.36, 1]
        }} data-id="element-38">
              <span className="grid h-14 w-14 place-items-center rounded-lg bg-[#1D6FE8] text-2xl font-black" data-id="element-39">
                V
              </span>
              <span className="text-4xl font-bold tracking-[-0.06em] sm:text-5xl" data-id="element-40">
                vivo <span className="font-normal text-slate-400" data-id="element-41">media</span>
              </span>
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      <header className="fixed inset-x-0 top-0 z-50 border-b border-white/10 bg-[#0A0A0A]/90 backdrop-blur-xl" data-id="element-42">
        <nav className="mx-auto flex h-20 max-w-7xl items-center justify-between px-5 lg:px-8" aria-label="Navegación principal" data-id="element-43">
          <a href="#inicio" className="flex items-center gap-2.5" aria-label="Vivo Media, inicio" data-id="element-44">
            <span className="grid h-8 w-8 place-items-center rounded-md bg-gradient-to-br from-[#1B3A6B] to-[#1D6FE8] text-sm font-black" data-id="element-45">
              V
            </span>
            <span className="text-lg font-bold tracking-[-0.04em]" data-id="element-46">
              vivo <span className="font-normal text-slate-400" data-id="element-47">media</span>
            </span>
          </a>
          <div className="hidden items-center gap-7 text-sm text-slate-300 lg:flex" data-id="element-48">
            <a href="#servicios" className="transition hover:text-[#4A90D9]" data-id="element-49">
              Servicios
            </a>
            <a href="#proyectos" className="transition hover:text-[#4A90D9]" data-id="element-50">
              Proyectos
            </a>
            <a href="#proceso" className="transition hover:text-[#4A90D9]" data-id="element-51">
              Proceso
            </a>
            <a href="#contacto" className="transition hover:text-[#4A90D9]" data-id="element-52">
              Contacto
            </a>
          </div>
          <a href="#servicios" className="hidden rounded-md bg-[#1D6FE8] px-4 py-2 text-sm font-bold text-white transition hover:bg-[#4A90D9] lg:block" data-id="element-53">
            Ver nuestros servicios
          </a>
          <button onClick={() => setMenuOpen(!menuOpen)} className="grid h-10 w-10 place-items-center rounded-md border border-[#1E2D45] lg:hidden" aria-label={menuOpen ? 'Cerrar menú' : 'Abrir menú'} aria-expanded={menuOpen} data-id="element-54">
            {menuOpen ? <XIcon className="h-5 w-5" data-id="element-55" /> : <MenuIcon className="h-5 w-5" data-id="element-56" />}
          </button>
        </nav>
        <AnimatePresence data-id="element-57">
          {menuOpen && <motion.div initial={{
          opacity: 0,
          height: 0
        }} animate={{
          opacity: 1,
          height: 'auto'
        }} exit={{
          opacity: 0,
          height: 0
        }} className="border-t border-[#1E2D45] bg-[#0D1117] px-5 py-5 lg:hidden" data-id="element-58">
              <div className="flex flex-col gap-4 text-sm text-slate-200" data-id="element-59">
                <a onClick={closeMenu} href="#servicios" data-id="element-60">
                  Servicios
                </a>
                <a onClick={closeMenu} href="#proyectos" data-id="element-61">
                  Proyectos
                </a>
                <a onClick={closeMenu} href="#proceso" data-id="element-62">
                  Proceso
                </a>
                <a onClick={closeMenu} href="#contacto" data-id="element-63">
                  Contacto
                </a>
              </div>
            </motion.div>}
        </AnimatePresence>
      </header>

      <main data-id="element-64">
        <section id="inicio" className="relative flex min-h-screen items-end overflow-hidden pb-16 pt-28 sm:items-center sm:pb-0" data-id="element-65">
          <img src={assets.heroLed} alt="Pantalla LED de gran formato en una plaza urbana" className="absolute inset-0 h-full w-full object-cover object-center" data-id="element-66" />
          <div className="absolute inset-0 bg-black/[0.72]" data-id="element-67" />
          <motion.div initial={{
          opacity: 0,
          y: 26
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.75,
          ease: 'easeOut'
        }} className="relative mx-auto w-full max-w-7xl px-5 text-center lg:px-8" style={{
          textShadow: '0 2px 20px rgba(0,0,0,0.8)'
        }} data-id="element-68">
            <div className="mb-6 flex items-center justify-center gap-3" data-id="element-69">
              <span className="grid h-10 w-10 place-items-center rounded-md bg-gradient-to-br from-[#1B3A6B] to-[#1D6FE8] text-base font-black" data-id="element-70">
                V
              </span>
              <span className="text-2xl font-bold tracking-[-0.04em]" data-id="element-71">
                vivo <span className="font-normal text-slate-300" data-id="element-72">media</span>
              </span>
            </div>
            <h1 className="mx-auto max-w-4xl text-5xl font-bold leading-[0.96] tracking-[-0.065em] text-white sm:text-7xl lg:text-[92px]" data-id="element-73">
              <span className="text-[#1D6FE8]" data-id="element-74">Comunicación que impacta.</span>
              <br data-id="element-75" />
              <span className="text-white" data-id="element-76">Resultados que se ven.</span>
            </h1>
            <p className="mt-7 text-base text-slate-300 sm:text-lg" data-id="element-77">
              Cartelería <span className="text-[#1D6FE8]" data-id="element-78">LED</span>{' '}
              <span className="mx-1 text-[#1D6FE8]" data-id="element-79">·</span>
              Marketing <span className="text-[#1D6FE8]" data-id="element-80">Digital</span>{' '}
              <span className="mx-1 text-[#1D6FE8]" data-id="element-81">·</span>
              Diseño <span className="mx-1 text-[#1D6FE8]" data-id="element-82">·</span> Audiovisual
            </p>
            <div className="pb-24 sm:pb-0" data-id="element-83" />
          </motion.div>
          <div className="absolute inset-x-0 bottom-0 border-t border-white/10 bg-[#0A0A0A]/70 backdrop-blur-sm" data-id="element-84">
            <div className="mx-auto flex max-w-7xl flex-col gap-3 px-5 py-4 sm:flex-row sm:items-center lg:px-8" data-id="element-85">
              <span className="shrink-0 text-[10px] font-bold tracking-[0.18em] text-slate-400" data-id="element-86">
                CONFÍAN EN NOSOTROS
              </span>
              <div className="relative min-w-0 flex-1 overflow-hidden" aria-label="Clientes que confían en Vivo Media" data-id="element-87">
                <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-10 bg-gradient-to-r from-[#0A0A0A] to-transparent" data-id="element-88" />
                <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-10 bg-gradient-to-l from-[#0A0A0A] to-transparent" data-id="element-89" />
                <motion.div className="flex w-max items-center gap-10 pr-10 text-sm font-semibold tracking-[0.12em] text-slate-400 sm:gap-14 sm:pr-14" animate={shouldReduceMotion ? {
                x: 0
              } : {
                x: '-50%'
              }} transition={shouldReduceMotion ? {
                duration: 0
              } : {
                duration: 26,
                ease: 'linear',
                repeat: Infinity
              }} data-id="element-90">
                  {[...clientLogos, ...clientLogos].map((logo, index) => <span key={`${logo}-${index}`} className="whitespace-nowrap" data-id="element-91">
                      {logo}
                    </span>)}
                </motion.div>
              </div>
            </div>
          </div>
        </section>

        <section id="servicios" className="bg-[#0A0A0A] px-5 py-24 lg:px-8 lg:py-32" data-id="element-92">
          <div className="mx-auto max-w-7xl" data-id="element-93">
            <SectionHeader eyebrow="LO QUE HACEMOS" title="Soluciones integrales para tu marca" description="Construimos presencias que iluminan el espacio físico, el digital y la memoria de las personas." data-id="element-94" />
            <div className="mt-14 grid gap-5 md:grid-cols-2 xl:grid-cols-4" data-id="element-95">
              {services.map((service, index) => {
              const Icon = service.icon;
              return <motion.article key={service.title} initial={{
                opacity: 0,
                y: 22
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                delay: index * 0.08
              }} className="group overflow-hidden rounded-xl border border-[#1E2D45] bg-[#111827] transition duration-300 hover:border-[#1D6FE8] hover:shadow-[0_0_25px_#1D6FE855]" data-id="element-96">
                    <div className="relative h-48 overflow-hidden" data-id="element-97">
                      <img src={service.image} alt="" className="h-full w-full object-cover transition duration-500 group-hover:scale-105 group-hover:brightness-110" data-id="element-98" />
                      <div className="absolute inset-0 bg-[#1D6FE8]/15 mix-blend-color" data-id="element-99" />
                    </div>
                    <div className="p-6" data-id="element-100">
                      <Icon className="h-5 w-5 text-[#1D6FE8]" aria-hidden="true" data-id="element-101" />
                      <h3 className="mt-5 text-xl font-semibold tracking-[-0.03em]" data-id="element-102">
                        {service.title}
                      </h3>
                      <p className="mt-2 text-sm leading-6 text-slate-400" data-id="element-103">
                        {service.description}
                      </p>
                      <ul className="mt-5 space-y-2 border-t border-[#1E2D45] pt-4" data-id="element-104">
                        {service.items.map(item => <li key={item} className="flex items-center gap-2 text-xs text-slate-300" data-id="element-105">
                            <span className="h-1 w-1 rounded-full bg-[#1D6FE8]" data-id="element-106" />
                            {item}
                          </li>)}
                      </ul>
                    </div>
                  </motion.article>;
            })}
            </div>
          </div>
        </section>

        <section className="relative overflow-hidden px-5 py-24 lg:px-8 lg:py-28" data-id="element-107">
          <img src={assets.ledWide} alt="Gran pantalla urbana con anuncios digitales" className="absolute inset-0 h-full w-full object-cover" data-id="element-108" />
          <div className="absolute inset-0 bg-[#0D1117]/80" data-id="element-109" />
          <div className="absolute inset-0 bg-[#1D6FE8]/15 mix-blend-color" data-id="element-110" />
          <div className="relative mx-auto grid max-w-7xl grid-cols-2 gap-y-12 md:grid-cols-4" data-id="element-111">
            {[['+10', 'Años creando'], ['+200', 'Proyectos'], ['+80', 'Clientes'], ['04', 'Áreas expertas']].map(([number, label]) => <div key={label} className="border-l border-[#1D6FE8]/70 pl-5" data-id="element-112">
                <p className="text-4xl font-semibold tracking-[-0.05em] text-[#4A90D9] sm:text-5xl" data-id="element-113">
                  {number}
                </p>
                <p className="mt-2 text-sm text-slate-200" data-id="element-114">{label}</p>
              </div>)}
          </div>
        </section>

        <section id="proyectos" className="bg-[#0D1117] px-5 py-24 lg:px-8 lg:py-32" data-id="element-115">
          <div className="mx-auto max-w-7xl" data-id="element-116">
            <div className="flex flex-col justify-between gap-8 md:flex-row md:items-end" data-id="element-117">
              <SectionHeader eyebrow="TRABAJO SELECCIONADO" title="Proyectos que hablan por sí solos" data-id="element-118" />
              <div className="flex flex-wrap gap-2" role="tablist" aria-label="Filtro de proyectos" data-id="element-119">
                {(['Todos', 'LED', 'Diseño', 'Digital', 'Audiovisual'] as PortfolioCategory[]).map(tab => <button role="tab" aria-selected={filter === tab} onClick={() => setFilter(tab)} key={tab} className={`rounded-full px-4 py-2 text-xs font-semibold transition ${filter === tab ? 'bg-[#1D6FE8] text-white' : 'border border-[#1E2D45] text-slate-400 hover:border-[#1D6FE8] hover:text-white'}`} data-id="element-120">
                    {tab}
                  </button>)}
              </div>
            </div>
            <motion.div layout className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-id="element-121">
              {filteredProjects.map(project => <motion.article layout initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} exit={{
              opacity: 0
            }} key={project.name} className="group relative aspect-[4/3] overflow-hidden rounded-xl border border-[#1E2D45]" data-id="element-122">
                  <img src={project.image} alt={project.name} className="h-full w-full object-cover transition duration-500 group-hover:scale-105" data-id="element-123" />
                  <div className="absolute inset-0 bg-[#1D6FE8]/15 mix-blend-color" data-id="element-124" />
                  <div className="absolute inset-0 bg-[#0D1117]/10 transition group-hover:bg-[#0D1117]/70" data-id="element-125" />
                  <div className="absolute inset-x-0 bottom-0 flex translate-y-4 items-end justify-between p-6 opacity-0 transition duration-300 group-hover:translate-y-0 group-hover:opacity-100" data-id="element-126">
                    <div data-id="element-127">
                      <p className="text-[10px] font-bold tracking-[0.16em] text-[#4A90D9]" data-id="element-128">
                        {project.category}
                      </p>
                      <h3 className="mt-2 text-xl font-semibold" data-id="element-129">
                        {project.name}
                      </h3>
                    </div>
                    <MoveUpRightIcon className="h-5 w-5" data-id="element-130" />
                  </div>
                </motion.article>)}
            </motion.div>
          </div>
        </section>

        <section id="proceso" className="relative overflow-hidden px-5 py-24 lg:px-8 lg:py-32" data-id="element-131">
          <img src={assets.workspace} alt="Espacio de trabajo digital" className="absolute inset-0 h-full w-full object-cover" data-id="element-132" />
          <div className="absolute inset-0 bg-[#0D1117]/85" data-id="element-133" />
          <div className="absolute inset-0 bg-[#1D6FE8]/15 mix-blend-color" data-id="element-134" />
          <div className="relative mx-auto max-w-7xl" data-id="element-135">
            <SectionHeader eyebrow="CÓMO TRABAJAMOS" title="De la idea al impacto" data-id="element-136" />
            <div className="relative mt-16 grid gap-9 md:grid-cols-4" data-id="element-137">
              <div className="absolute left-[12.5%] right-[12.5%] top-7 hidden h-px bg-[#1D6FE8]/80 md:block" data-id="element-138" />
              {[[MessageCircleIcon, '01', 'Brief', 'Escuchamos y entendemos el desafío.'], [ZapIcon, '02', 'Estrategia', 'Transformamos objetivos en dirección.'], [ClapperboardIcon, '03', 'Producción', 'Damos forma a cada punto de contacto.'], [SendIcon, '04', 'Lanzamiento', 'Activamos, medimos y optimamos.']].map(([Icon, number, title, copy]) => {
              const StepIcon = Icon as typeof MessageCircleIcon;
              return <div className="relative" key={title as string} data-id="element-139">
                    <div className="relative z-10 grid h-14 w-14 place-items-center rounded-full border border-[#1D6FE8] bg-[#0D1117]" data-id="element-140">
                      <StepIcon className="h-5 w-5 text-[#4A90D9]" data-id="element-141" />
                    </div>
                    <p className="mt-6 text-xs font-bold tracking-[0.18em] text-[#4A90D9]" data-id="element-142">
                      {number as string}
                    </p>
                    <h3 className="mt-2 text-xl font-semibold" data-id="element-143">
                      {title as string}
                    </h3>
                    <p className="mt-2 max-w-xs text-sm leading-6 text-slate-300" data-id="element-144">
                      {copy as string}
                    </p>
                  </div>;
            })}
            </div>
          </div>
        </section>

        <section id="nosotros" className="bg-[#0A0A0A] px-5 py-24 lg:px-8 lg:py-32" data-id="element-145">
          <div className="mx-auto grid max-w-7xl items-center gap-12 lg:grid-cols-2 lg:gap-20" data-id="element-146">
            <motion.div initial={{
            opacity: 0,
            x: -24
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true,
            amount: 0.3
          }} transition={{
            duration: 0.6
          }} className="relative overflow-hidden rounded-2xl border border-[#1E2D45] aspect-[4/3]" data-id="element-147">
              <img src={assets.workspace} alt="Equipo creativo de Vivo Media trabajando en un proyecto" className="h-full w-full object-cover" data-id="element-148" />
              <div className="absolute inset-0 bg-[#0D1117]/40" data-id="element-149" />
              <div className="absolute inset-0 bg-[#1D6FE8]/15 mix-blend-color" data-id="element-150" />
              <div className="absolute bottom-5 left-5 border-l-2 border-[#1D6FE8] bg-[#0A0A0A]/85 px-4 py-3 backdrop-blur-sm" data-id="element-151">
                <p className="text-[10px] font-bold tracking-[0.18em] text-[#4A90D9]" data-id="element-152">
                  DESDE MONTEVIDEO
                </p>
                <p className="mt-1 text-sm font-semibold text-white" data-id="element-153">
                  Ideas con impacto real
                </p>
              </div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            x: 24
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true,
            amount: 0.3
          }} transition={{
            duration: 0.6,
            delay: 0.08
          }} data-id="element-154">
              <p className="text-[11px] font-bold tracking-[0.22em] text-[#1D6FE8]" data-id="element-155">
                QUIÉNES SOMOS
              </p>
              <h2 className="mt-5 max-w-xl text-4xl font-semibold leading-[1.05] tracking-[-0.04em] sm:text-5xl" data-id="element-156">
                Creatividad, tecnología y estrategia para hacer que las marcas
                se muevan.
              </h2>
              <div className="mt-7 max-w-xl space-y-5 text-base leading-7 text-slate-300" data-id="element-157">
                <p data-id="element-158">
                  En Vivo Media somos una agencia integral de comunicación que
                  transforma buenas ideas en experiencias visibles, memorables y
                  medibles.
                </p>
                <p data-id="element-159">
                  Combinamos cartelería LED, diseño, marketing digital y
                  producción audiovisual para construir marcas con presencia en
                  cada punto de contacto.
                </p>
              </div>
              <div className="mt-9 grid max-w-xl grid-cols-3 gap-4 border-t border-[#1E2D45] pt-6" data-id="element-160">
                <div data-id="element-161">
                  <p className="text-2xl font-semibold tracking-[-0.04em] text-[#4A90D9]" data-id="element-162">
                    10+
                  </p>
                  <p className="mt-1 text-xs text-slate-400" data-id="element-163">
                    años de experiencia
                  </p>
                </div>
                <div data-id="element-164">
                  <p className="text-2xl font-semibold tracking-[-0.04em] text-[#4A90D9]" data-id="element-165">
                    200+
                  </p>
                  <p className="mt-1 text-xs text-slate-400" data-id="element-166">
                    proyectos realizados
                  </p>
                </div>
                <div data-id="element-167">
                  <p className="text-2xl font-semibold tracking-[-0.04em] text-[#4A90D9]" data-id="element-168">
                    01
                  </p>
                  <p className="mt-1 text-xs text-slate-400" data-id="element-169">equipo integral</p>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        <section className="relative overflow-hidden px-5 py-28 text-center lg:px-8 lg:py-40" data-id="element-170">
          <img src={assets.ledWide} alt="Edificio con una gran pantalla publicitaria" className="absolute inset-0 h-full w-full object-cover" data-id="element-171" />
          <div className="absolute inset-0 bg-[#0D1117]/75" data-id="element-172" />
          <div className="absolute inset-0 bg-[#1D6FE8]/15 mix-blend-color" data-id="element-173" />
          <div className="relative mx-auto max-w-3xl" data-id="element-174">
            <p className="text-[11px] font-bold tracking-[0.2em] text-[#4A90D9]" data-id="element-175">
              TU MARCA, EN MOVIMIENTO
            </p>
            <h2 className="mt-5 text-4xl font-semibold tracking-[-0.05em] sm:text-6xl" data-id="element-176">
              ¿Listo para llevar tu marca al siguiente nivel?
            </h2>
            <a href="#contacto" className="mt-9 inline-flex items-center gap-2 rounded-md bg-white px-6 py-4 text-sm font-bold text-[#0A0A0A] transition hover:bg-[#4A90D9] hover:text-white" data-id="element-177">
              Quiero empezar ahora <ArrowRightIcon className="h-4 w-4" data-id="element-178" />
            </a>
            <a href="https://wa.me/59800000000" className="mt-5 block text-sm text-slate-300 transition hover:text-white" data-id="element-179">
              O escribinos por WhatsApp
            </a>
          </div>
        </section>

        <section id="contacto" className="bg-[#0D1117] px-5 py-24 lg:px-8 lg:py-32" data-id="element-180">
          <div className="mx-auto grid max-w-7xl overflow-hidden rounded-2xl border border-[#1E2D45] lg:grid-cols-2" data-id="element-181">
            <div className="relative min-h-[460px] p-8 sm:p-12" data-id="element-182">
              <img src={assets.workspace} alt="Equipo creativo trabajando frente a pantallas" className="absolute inset-0 h-full w-full object-cover" data-id="element-183" />
              <div className="absolute inset-0 bg-[#0D1117]/75" data-id="element-184" />
              <div className="absolute inset-0 bg-[#1D6FE8]/15 mix-blend-color" data-id="element-185" />
              <div className="relative" data-id="element-186">
                <p className="text-[11px] font-bold tracking-[0.2em] text-[#4A90D9]" data-id="element-187">
                  HABLEMOS
                </p>
                <h2 className="mt-5 max-w-md text-4xl font-semibold tracking-[-0.05em]" data-id="element-188">
                  El próximo gran proyecto puede empezar hoy.
                </h2>
                <div className="mt-12 space-y-5 text-sm text-slate-200" data-id="element-189">
                  <p className="flex items-center gap-3" data-id="element-190">
                    <MailIcon className="h-4 w-4 text-[#1D6FE8]" data-id="element-191" />
                    hola@vivomedia.uy
                  </p>
                  <p className="flex items-center gap-3" data-id="element-192">
                    <MessageCircleIcon className="h-4 w-4 text-[#1D6FE8]" data-id="element-193" />
                    +598 99 000 000
                  </p>
                  <p className="flex items-center gap-3" data-id="element-194">
                    <MapPinIcon className="h-4 w-4 text-[#1D6FE8]" data-id="element-195" />
                    Montevideo, Uruguay
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-[#0A0A0A] p-5 sm:p-9" data-id="element-196">
              <ContactForm data-id="element-197" />
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t-2 border-[#1D6FE8] bg-[#0A0A0A] px-5 pt-16 lg:px-8" data-id="element-198">
        <div className="mx-auto grid max-w-7xl gap-12 pb-14 md:grid-cols-[1.6fr_1fr_1fr]" data-id="element-199">
          <div data-id="element-200">
            <a href="#inicio" className="text-2xl font-bold tracking-[-0.05em]" data-id="element-201">
              vivo <span className="font-normal text-slate-400" data-id="element-202">media</span>
            </a>
            <p className="mt-4 max-w-sm text-sm leading-6 text-slate-400" data-id="element-203">
              Ideas, tecnología y estética para que las marcas ocupen el lugar
              que merecen.
            </p>
          </div>
          <div data-id="element-204">
            <p className="text-xs font-bold tracking-[0.16em] text-[#4A90D9]" data-id="element-205">
              NAVEGAR
            </p>
            <div className="mt-5 flex flex-col gap-3 text-sm text-slate-400" data-id="element-206">
              <a href="#servicios" className="transition hover:text-white" data-id="element-207">
                Servicios
              </a>
              <a href="#proyectos" className="transition hover:text-white" data-id="element-208">
                Proyectos
              </a>
              <a href="#proceso" className="transition hover:text-white" data-id="element-209">
                Proceso
              </a>
              <a href="#contacto" className="transition hover:text-white" data-id="element-210">
                Contacto
              </a>
            </div>
          </div>
          <div data-id="element-211">
            <p className="text-xs font-bold tracking-[0.16em] text-[#4A90D9]" data-id="element-212">
              SEGUINOS
            </p>
            <div className="mt-5 flex gap-3" data-id="element-213">
              <a aria-label="Instagram" href="#" className="grid h-9 w-9 place-items-center rounded border border-[#1E2D45] text-slate-400 transition hover:border-[#1D6FE8] hover:text-white" data-id="element-214">
                <InstagramIcon className="h-4 w-4" data-id="element-215" />
              </a>
              <a aria-label="LinkedIn" href="#" className="grid h-9 w-9 place-items-center rounded border border-[#1E2D45] text-slate-400 transition hover:border-[#1D6FE8] hover:text-white" data-id="element-216">
                <LinkedinIcon className="h-4 w-4" data-id="element-217" />
              </a>
              <a aria-label="Facebook" href="#" className="grid h-9 w-9 place-items-center rounded border border-[#1E2D45] text-slate-400 transition hover:border-[#1D6FE8] hover:text-white" data-id="element-218">
                <FacebookIcon className="h-4 w-4" data-id="element-219" />
              </a>
              <a aria-label="Dribbble" href="#" className="grid h-9 w-9 place-items-center rounded border border-[#1E2D45] text-slate-400 transition hover:border-[#1D6FE8] hover:text-white" data-id="element-220">
                <DribbbleIcon className="h-4 w-4" data-id="element-221" />
              </a>
            </div>
          </div>
        </div>
        <div className="mx-auto flex max-w-7xl flex-col justify-between gap-2 border-t border-[#1E2D45] py-6 text-xs text-slate-500 sm:flex-row" data-id="element-222">
          <span data-id="element-223">© 2025 Vivo Media. Todos los derechos reservados.</span>
          <span data-id="element-224">Hecho con propósito en Uruguay</span>
        </div>
      </footer>

      <div className="fixed bottom-5 right-5 z-40" data-id="element-225">
        <span aria-hidden="true" className="pointer-events-none absolute inset-0 rounded-full border border-[#64B5FF]/80 motion-safe:animate-[ping_1.8s_cubic-bezier(0,0,0.2,1)_infinite]" data-id="element-226" />
        <a href="https://wa.me/59800000000" aria-label="Abrir WhatsApp" className="relative grid h-14 w-14 place-items-center rounded-full bg-gradient-to-br from-[#1B3A6B] to-[#1D6FE8] text-white shadow-[0_0_0_0_rgba(29,111,232,0.75)] transition hover:scale-105 focus:outline-none focus:ring-2 focus:ring-[#64B5FF] focus:ring-offset-2 focus:ring-offset-[#0A0A0A] motion-safe:animate-[pulse_1.8s_ease-in-out_infinite]" data-id="element-227">
          <MessageCircleIcon className="h-6 w-6" data-id="element-228" />
        </a>
      </div>
    </div>;
}